import { useEffect } from "react";

// =============================================================================
// While a View Transition runs, the page is a snapshot that cannot be hit: a
// click lands on the document and is lost, so a link clicked right after a
// page change seemed to need a second click.
// =============================================================================

let runningTransition: ViewTransition | null = null;

/** Remembers a View Transition until it is over */
export function trackViewTransition(transition: ViewTransition) {
  runningTransition = transition;
  const clear = () => {
    if (runningTransition === transition) runningTransition = null;
  };
  transition.finished.then(clear, clear);
  return transition;
}

/**
 * A click during a page transition ends it at once, then reaches the link or
 * button under the pointer.
 */
export function useClickThroughTransitions() {
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      const transition = runningTransition;
      if (!transition || event.target !== document.documentElement) return;

      transition.skipTransition();
      // Plain clicks only: a replayed click would lose its modifier keys
      if (
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }
      const control = document
        .elementFromPoint(event.clientX, event.clientY)
        ?.closest<HTMLElement>("a[href], button:not(:disabled)");
      if (!control) return;

      event.preventDefault();
      event.stopPropagation();
      control.click();
    };

    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);
}

// =============================================================================
